import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionProvider } from './contexts/PermissionContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import CreateBundle from './pages/CreateBundle';
import Debundle from './pages/Debundle';
import BundleToolTables from './pages/BundleToolTables';
import InventoryPage from './pages/InventoryPage';
import InventoryShelfPage from './pages/InventoryShelfPage';
import ActivityLogsPage from './pages/ActivityLogs';
import SettingsPage from './pages/SettingsPage';
import AdminCreateUser from './pages/AdminCreateUser';
import AdminUsersList from './pages/AdminUsersList';
import AccessControl from './pages/AccessControl';
import DraftsPage from './pages/DraftsPage';
import BundleCombination from './pages/BundleCombination';
import ImageGeneration from './pages/ImageGeneration';
import CyclicCountPage from './pages/CyclicCountPage';
import CardingPage from './pages/CardingPage';
import PermissionRoute from './components/auth/PermissionRoute';
import ActionablesPage from './pages/ActionablesPage';
import { ToastProvider } from './components/common/ToastProvider';
import './App.css';


// Salty brand theme — the single source of truth for the orange / beige / ink token system.
// Downstream features import `tokens` instead of re-deriving literals.
const NIMBUS_STACK = '"Nimbus Sans L", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Warm-neutral ramp replacing the legacy violet-era grey ramp.
const warmGrey = {
  50: '#FAFAF7',
  100: '#F3F2EC',
  200: '#ECECE4',
  300: '#DAD6CC',
  400: '#B8B2A6',
  500: '#8A8078',
  600: '#6B625B',
  700: '#4A433D',
  800: '#2E2822',
  900: '#1A1410',
};

// Salty design tokens now live in ./theme/tokens (a leaf module) to avoid an App<->Dashboard
// circular import that threw "Cannot access 'tokens' before initialization" at runtime.
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#DC5829', // Salty Orange
      light: '#F26A2E',
      dark: '#B8451E',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#6B625B', // Muted warm-neutral (replaces teal accent)
      light: '#8A8078',
      dark: '#4A433D',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F1F2DA', // Salty Beige
      paper: '#FFFFFF', // Pure white cards
    },
    text: {
      primary: '#1A1410', // Salty Ink
      secondary: '#6B625B', // Warm grey
    },
    error: {
      main: '#DC2626',
      light: '#EF4444',
      dark: '#B91C1C',
    },
    warning: {
      main: '#D97706',
      light: '#F59E0B',
      dark: '#B45309',
    },
    success: {
      main: '#16A34A',
      light: '#22C55E',
      dark: '#15803D',
    },
    info: {
      main: '#B8451E', // Restrained orange-tint (replaces teal)
      light: '#DC5829',
      dark: '#8A3516',
    },
    grey: warmGrey,
    divider: '#ECECE4',
  },
  typography: {
    fontFamily: NIMBUS_STACK,
    h1: {
      fontFamily: NIMBUS_STACK,
      fontWeight: 700,
      fontSize: '2.75rem',
      lineHeight: 1.1,
      letterSpacing: '-0.04em',
    },
    h2: {
      fontFamily: NIMBUS_STACK,
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.15,
      letterSpacing: '-0.03em',
    },
    h3: {
      fontFamily: NIMBUS_STACK,
      fontWeight: 700,
      fontSize: '1.875rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontFamily: NIMBUS_STACK,
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.25,
      letterSpacing: '-0.015em',
    },
    h5: {
      fontFamily: NIMBUS_STACK,
      fontWeight: 700,
      fontSize: '1.25rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontFamily: NIMBUS_STACK,
      fontWeight: 700,
      fontSize: '1.125rem',
      lineHeight: 1.4,
      letterSpacing: '-0.005em',
    },
    subtitle1: {
      fontFamily: NIMBUS_STACK,
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.01em',
    },
    subtitle2: {
      fontFamily: NIMBUS_STACK,
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.57,
      letterSpacing: '0.01em',
    },
    body1: {
      fontFamily: NIMBUS_STACK,
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.65,
      letterSpacing: '0.01em',
    },
    body2: {
      fontFamily: NIMBUS_STACK,
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.57,
      letterSpacing: '0.01em',
    },
    caption: {
      fontFamily: NIMBUS_STACK,
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.02em',
    },
    overline: {
      fontFamily: NIMBUS_STACK,
      fontWeight: 700,
      fontSize: '0.68rem',
      lineHeight: 1.4,
      letterSpacing: '0.1em',
      textTransform: 'uppercase' as const,
    },
    button: {
      fontFamily: NIMBUS_STACK,
      fontWeight: 600,
      textTransform: 'none' as const,
      letterSpacing: '0.02em',
      fontSize: '0.875rem',
    },
  },
  shape: {
    borderRadius: 12, // 0.75rem
  },
  // Subtle warm shadow ramp (MUI requires exactly 25 entries). Low indices mirror
  // tableStyles/Actionables soft layering; the ramp scales up gently.
  shadows: [
    'none',
    '0 1px 2px rgba(26,20,15,0.04), 0 10px 28px rgba(26,20,15,0.05)',
    '0 1px 3px rgba(26,20,15,0.05), 0 12px 30px rgba(26,20,15,0.06)',
    '0 2px 4px rgba(26,20,15,0.05), 0 14px 34px rgba(26,20,15,0.06)',
    '0 2px 6px rgba(26,20,15,0.06), 0 16px 38px rgba(26,20,15,0.07)',
    '0 3px 8px rgba(26,20,15,0.06), 0 18px 42px rgba(26,20,15,0.07)',
    '0 4px 10px rgba(26,20,15,0.06), 0 20px 46px rgba(26,20,15,0.08)',
    '0 5px 12px rgba(26,20,15,0.07), 0 22px 50px rgba(26,20,15,0.08)',
    '0 6px 14px rgba(26,20,15,0.07), 0 24px 54px rgba(26,20,15,0.09)',
    '0 7px 16px rgba(26,20,15,0.07), 0 26px 58px rgba(26,20,15,0.09)',
    '0 8px 18px rgba(26,20,15,0.08), 0 28px 62px rgba(26,20,15,0.10)',
    '0 9px 20px rgba(26,20,15,0.08), 0 30px 66px rgba(26,20,15,0.10)',
    '0 10px 22px rgba(26,20,15,0.08), 0 32px 70px rgba(26,20,15,0.11)',
    '0 11px 24px rgba(26,20,15,0.09), 0 34px 74px rgba(26,20,15,0.11)',
    '0 12px 26px rgba(26,20,15,0.09), 0 36px 78px rgba(26,20,15,0.12)',
    '0 13px 28px rgba(26,20,15,0.09), 0 38px 82px rgba(26,20,15,0.12)',
    '0 14px 30px rgba(26,20,15,0.10), 0 40px 86px rgba(26,20,15,0.13)',
    '0 15px 32px rgba(26,20,15,0.10), 0 42px 90px rgba(26,20,15,0.13)',
    '0 16px 34px rgba(26,20,15,0.10), 0 44px 94px rgba(26,20,15,0.14)',
    '0 17px 36px rgba(26,20,15,0.11), 0 46px 98px rgba(26,20,15,0.14)',
    '0 18px 38px rgba(26,20,15,0.11), 0 48px 102px rgba(26,20,15,0.15)',
    '0 19px 40px rgba(26,20,15,0.11), 0 50px 106px rgba(26,20,15,0.15)',
    '0 20px 42px rgba(26,20,15,0.12), 0 52px 110px rgba(26,20,15,0.16)',
    '0 21px 44px rgba(26,20,15,0.12), 0 54px 114px rgba(26,20,15,0.16)',
    '0 22px 46px rgba(26,20,15,0.12), 0 56px 118px rgba(26,20,15,0.17)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 20px',
          boxShadow: 'none',
          letterSpacing: '0.025em',
          fontSize: '0.875rem',
          minHeight: '44px', // Touch target
          transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
          border: '1px solid transparent',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0px 4px 16px rgba(220, 88, 41, 0.3)',
          },
        },
        contained: {
          boxShadow: '0px 2px 8px rgba(220, 88, 41, 0.2)',
          '&:hover': {
            boxShadow: '0px 6px 20px rgba(220, 88, 41, 0.3)',
          },
          '&.MuiButton-containedPrimary': {
            color: '#fff', // pin white label so sx fill overrides keep readable text (contrast fix)
            background: 'linear-gradient(135deg, #DC5829 0%, #B8451E 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #F26A2E 0%, #DC5829 100%)',
            },
          },
        },
        outlined: {
          borderColor: '#C9C2B4', // warm-grey neutral; reads clearly on white cards and beige page (divider #ECECE4 is too faint)
          borderWidth: '1.5px',
          color: '#1A1410',
          '&:hover': {
            borderColor: '#6B625B', // darken toward text.secondary
            borderWidth: '1.5px', // keep 1.5px so hover doesn't fall back to MUI's default 1px
            backgroundColor: 'rgba(220, 88, 41, 0.06)', // subtle warm tint, matches the tool's pill-button hovers
          },
          '&.MuiButton-outlinedPrimary': {
            borderColor: 'rgba(220, 88, 41, 0.5)', // primary-variant outlined buttons keep a primary-tinted border
            '&:hover': {
              borderColor: '#DC5829',
              borderWidth: '1.5px',
              backgroundColor: 'rgba(220, 88, 41, 0.06)',
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #ECECE4',
          boxShadow: '0 1px 2px rgba(26,20,15,0.04), 0 10px 28px rgba(26,20,15,0.05)',
          backgroundColor: '#FFFFFF',
          backgroundImage: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 6px 14px rgba(26,20,15,0.07), 0 24px 54px rgba(26,20,15,0.09)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#FFFFFF',
          backgroundImage: 'none',
          border: '1px solid #ECECE4',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#1A1410',
          borderBottom: '1px solid #ECECE4',
          boxShadow: '0 1px 2px rgba(26,20,15,0.04)',
          backdropFilter: 'blur(12px)',
          backgroundImage: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          borderRight: 'none',
          backgroundColor: '#1A1410', // Salty near-black drawer
          backgroundImage: 'linear-gradient(180deg, #1A1410 0%, #2A211C 100%)',
          color: '#ffffff',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
          letterSpacing: '0.025em',
          fontSize: '0.75rem',
          border: '1px solid transparent',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#FFFFFF',
            '& fieldset': {
              borderColor: '#ECECE4',
            },
            '&:hover fieldset': {
              borderColor: '#DC5829',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#DC5829',
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#F3F2EC',
          '& .MuiTableCell-head': {
            color: '#6B625B',
            fontWeight: 700,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
        indicator: {
          backgroundColor: '#DC5829',
          height: 3,
          borderRadius: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: NIMBUS_STACK,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.9rem',
          letterSpacing: '0.01em',
          minHeight: 44,
          color: '#6B625B',
          '&.Mui-selected': {
            color: '#DC5829',
          },
        },
      },
    },
  },
});

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const HomeRoute: React.FC = () => <Dashboard />;

  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ToastProvider>
          <AuthProvider>
            <PermissionProvider>
            <Router>
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<HomeRoute />} />
                  {/* create-bundle route protected below by PermissionRoute */}
                  <Route
                    path="/activities"
                    element={
                      <PermissionRoute requiredFeature="activities">
                        <ActivityLogsPage />
                      </PermissionRoute>
                    }
                  />
                  {/* Bundles page removed */}
                  <Route
                    path="/inventory"
                    element={
                      <PermissionRoute requiredFeature="view-inventory">
                        <InventoryPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/inventory-shelf"
                    element={
                      <PermissionRoute requiredFeature="view-inventory">
                        <InventoryShelfPage />
                      </PermissionRoute>
                    }
                  />
                  {/* Reports and History pages removed */}
                  <Route
                    path="/settings"
                    element={
                      <PermissionRoute requiredFeature="settings">
                        <SettingsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/admin/create-user"
                    element={
                      // Creating users is a manage-users mutation (Admin only via the matrix).
                      <PermissionRoute requiredFeature="manage-users" level="edit">
                        <AdminCreateUser />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <PermissionRoute requiredFeature="manage-users">
                        <AdminUsersList />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/create-bundle"
                    element={
                      <PermissionRoute requiredFeature="add-bundle">
                        <CreateBundle />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/drafts"
                    element={
                      <PermissionRoute requiredFeature="saved-drafts">
                        <DraftsPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/debundle"
                    element={
                      <PermissionRoute requiredFeature="reduce-bundle">
                        <Debundle />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/bundle-categories"
                    element={
                      <PermissionRoute requiredFeature="bundle-skus">
                        <BundleToolTables />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/bundle-tables"
                    element={
                      <PermissionRoute requiredFeature="bundle-skus">
                        <BundleToolTables />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/actionables"
                    element={
                      /* Actionables is reachable by ANY authenticated user (not a registry
                         row); its content stays auto-scoped by assignment. This is the
                         intended net-new Photography grant. */
                      <ActionablesPage />
                    }
                  />
                  <Route
                    path="/bundle-combination"
                    element={
                      <PermissionRoute requiredFeature="bundle-combination">
                        <BundleCombination />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/image-generation"
                    element={
                      <PermissionRoute requiredFeature="image-generation">
                        <ImageGeneration />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/cyclic-count"
                    element={
                      <PermissionRoute requiredFeature="cyclic-count">
                        <CyclicCountPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/carding"
                    element={
                      <PermissionRoute requiredFeature="carding">
                        <CardingPage />
                      </PermissionRoute>
                    }
                  />
                  <Route
                    path="/access-control"
                    element={
                      // Admin-only Access Control center. Gate level MUST be 'edit' to
                      // match the Sidebar gate (visibility and reachability agree).
                      <PermissionRoute requiredFeature="access-control" level="edit">
                        <AccessControl />
                      </PermissionRoute>
                    }
                  />
                  {/* Catch-all: unknown (no-match) paths redirect to the ungated Dashboard
                      so the DEFAULT landing is always reachable. Declared LAST and with the
                      lowest match specificity, so every real route above (including every
                      PermissionRoute) still matches first — an explicit nav to a forbidden
                      route (e.g. /inventory) still renders its own Unauthorized panel. */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>

              </Layout>
            </ProtectedRoute>
          </Router>
            </PermissionProvider>
          </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </GoogleOAuthProvider>
    </QueryClientProvider>
  );
}

export default App;
